import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import Dialog from "./Dialog";
import {
  AnswerFormat,
  AnswerFormatType,
  MAX_CHOICE_OPTIONS,
  MAX_OPTION_LENGTH,
  OPEN_ENDED,
  YES_NO,
  cleanChoices,
  formatSummary,
  sameFormat,
} from "../utils/verdict";

const TYPES: Array<{ type: AnswerFormatType; label: string; hint: string }> = [
  { type: "yes_no", label: "Yes or no", hint: "The verdict shows how far the panel leans yes or no." },
  { type: "multiple_choice", label: "One of several options", hint: "The verdict shows each option's share of the panel." },
  { type: "open_ended", label: "Open answer", hint: "No verdict chart, just the written synthesis." },
];

/** Correct the detected answer format before a debate starts. */
export default function AnswerFormatDialog({
  open,
  onClose,
  format,
  detected,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  format: AnswerFormat | null;
  detected: AnswerFormat | null;
  onSave: (format: AnswerFormat) => void;
}) {
  const [type, setType] = useState<AnswerFormatType>("open_ended");
  const [choices, setChoices] = useState<string[]>(["", ""]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const start = format ?? detected ?? OPEN_ENDED;
    setType(start.type);
    setChoices(start.type === "multiple_choice" ? [...start.options] : ["", ""]);
    setError(null);
  }, [open, format, detected]);

  const save = () => {
    if (type === "yes_no") return onSave(YES_NO);
    if (type === "open_ended") return onSave(OPEN_ENDED);
    const cleaned = cleanChoices(choices);
    if (!cleaned) {
      setError(`Add 2 to ${MAX_CHOICE_OPTIONS} different options.`);
      return;
    }
    onSave({ type: "multiple_choice", options: cleaned });
  };

  const setChoice = (i: number, value: string) => {
    setChoices((list) => list.map((c, j) => (j === i ? value : c)));
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Answer format">
      <p className="text-sm text-muted leading-6">
        Each expert says how their argument leans, and the panel's average is shown above the synthesis.
      </p>
      <fieldset>
        <legend className="sr-only">How the question is answered</legend>
        <div className="border-y border-line divide-y divide-line">
          {TYPES.map((t) => (
            <label
              key={t.type}
              className="flex items-start gap-3 py-3 cursor-pointer rounded-sm hover:bg-subtle focus-within:bg-subtle transition-colors duration-150"
            >
              <input
                type="radio"
                name="answer-format"
                value={t.type}
                checked={type === t.type}
                onChange={() => {
                  setType(t.type);
                  setError(null);
                }}
                className="mt-1 h-4 w-4 accent-[rgb(var(--color-action))] focus-visible:outline-offset-2"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-content">{t.label}</span>
                <span className="block text-xs text-muted">{t.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {type === "multiple_choice" && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-content">Options</p>
          {choices.map((choice, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={choice}
                maxLength={MAX_OPTION_LENGTH}
                onChange={(e) => setChoice(i, e.target.value)}
                aria-label={`Option ${i + 1}`}
                placeholder={`Option ${i + 1}`}
                className="min-w-0 flex-1 h-10 px-3 rounded-md border border-line bg-surface text-sm text-content hover:border-control focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              />
              <button
                type="button"
                disabled={choices.length <= 2}
                onClick={() => setChoices((list) => list.filter((_, j) => j !== i))}
                aria-label={`Remove option ${i + 1}`}
                className="h-10 w-10 inline-flex items-center justify-center rounded-md text-muted hover:bg-subtle hover:text-content active:bg-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
          ))}
          {choices.length < MAX_CHOICE_OPTIONS && (
            <button
              type="button"
              onClick={() => setChoices((list) => [...list, ""])}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 -ml-2 rounded-md text-sm font-medium text-content hover:bg-subtle active:bg-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-150"
            >
              <Plus size={15} aria-hidden="true" />
              Add option
            </button>
          )}
        </div>
      )}

      {detected && !sameFormat(detected, format) && (
        <p className="text-xs text-muted">Detected from your topic: {formatSummary(detected)}</p>
      )}
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="button-secondary">
          Cancel
        </button>
        <button type="button" onClick={save} className="button-primary">
          Save
        </button>
      </div>
    </Dialog>
  );
}
